import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const g = (obj, m) => obj?.[m] ?? obj?.[String(m)];
const CAT_LABELS = { PROJECT: "Project", SERVICE: "Service", OTHER: "Other Revenue" };

export default function RevenueSourcePanel({ open, onClose, source, periods, year, canEdit, onChanged, onArchive }) {
  const [actualDrafts, setActualDrafts] = useState({});

  if (!source) return null;

  const changeRoute = async (v) => {
    try {
      await api.patch(`/revenue-sources/${source.id}`, { billing_route: v === "none" ? null : v });
      toast.success("Billing route updated");
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveActual = async (m) => {
    const d = actualDrafts[m] || {};
    try {
      await api.put("/revenue-months/actual", {
        revenue_source_id: source.id,
        year,
        month: m,
        actual_amount: d.amount === "" || d.amount == null ? null : Number(d.amount),
        actual_source: "MANUAL",
        verified: true,
      });
      toast.success(`${MONTHS[m - 1]} actual saved`);
      setActualDrafts({ ...actualDrafts, [m]: {} });
      await onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const closedMonths = MONTHS.map((_, i) => i + 1).filter((m) => g(periods, m)?.status === "CLOSED");

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="source-panel">
        <SheetHeader>
          <SheetTitle className="font-heading">{source.name}</SheetTitle>
          <SheetDescription>
            {CAT_LABELS[source.category]} · {source.customer_name || "No customer"} · {year}
          </SheetDescription>
        </SheetHeader>

        <div className="grid grid-cols-3 gap-4 mt-5">
          <div>
            <div className="text-xs text-slate-500">Expected FY</div>
            <div className="text-sm font-bold text-slate-900" data-testid="source-panel-fy">{fmtSEK(source.fy)} kr</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Actual YTD</div>
            <div className="text-sm font-bold text-slate-900" data-testid="source-panel-ytd">{fmtSEK(source.ytd)} kr</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Status</div>
            <div className="text-sm font-semibold text-slate-900">{source.active ? "Active" : "Inactive"}</div>
          </div>
        </div>

        {source.category === "OTHER" && (
          <div className="mt-4 flex items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid="source-billing-route-row">
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-700">Billing route (Group model)</div>
              <div className="text-[11px] text-slate-400">Other revenue is never guessed into Group pass-through — explicit classification required.</div>
            </div>
            {canEdit ? (
              <Select value={source.billing_route || "none"} onValueChange={changeRoute}>
                <SelectTrigger className="w-56" data-testid="source-billing-route-edit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not classified</SelectItem>
                  <SelectItem value="VIA_GROUP" data-testid="source-route-edit-via-group">Via Elva Group (pass-through)</SelectItem>
                  <SelectItem value="DIRECT" data-testid="source-route-edit-direct">Direct (billed by this company)</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${source.billing_route ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#FFFBEB] text-[#B45309]"}`} data-testid="source-billing-route-badge">
                {source.billing_route === "VIA_GROUP" ? "Via Group" : source.billing_route === "DIRECT" ? "Direct" : "Not classified"}
              </span>
            )}
          </div>
        )}

        <div className="mt-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Monthly values</h3><table className="w-full border border-slate-200 rounded-lg overflow-hidden" data-testid="source-panel-months">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase py-2 px-3">Month</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase py-2 px-3">Forecast</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase py-2 px-3">Actual</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((label, i) => {
                const m = i + 1;
                const cell = g(source.months, m) || {};
                const closed = g(periods, m)?.status === "CLOSED";
                return (
                  <tr key={m} className={`border-b border-slate-100 ${closed ? "bg-slate-50" : ""}`} data-testid={`source-panel-month-${m}`}>
                    <td className="py-2 px-3 text-sm font-semibold text-slate-800">
                      {label}
                      {closed && <span className="ml-1.5 text-[10px] font-semibold text-slate-500 uppercase">Closed</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-sm text-slate-700">{fmtSEK(cell.forecast_amount)}</td>
                    <td className="py-2 px-3 text-right text-sm font-semibold text-slate-900">
                      {closed ? (
                        cell.missing_actual ? (
                          <span className="text-[#B45309] text-xs font-semibold">Missing actual</span>
                        ) : (
                          fmtSEK(cell.actual_amount)
                        )
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {canEdit && closedMonths.length > 0 && (
          <div className="mt-6" data-testid="source-actuals-section">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Actual entry (closed months)</h3>
            <div className="space-y-2">
              {closedMonths.map((m) => {
                const cell = g(source.months, m) || {};
                const d = actualDrafts[m] || {};
                return (
                  <div key={m} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid={`source-actual-row-${m}`}>
                    <span className="text-sm font-semibold text-slate-800 w-10">{MONTHS[m - 1]}</span>
                    <Input
                      type="number"
                      className="h-8 w-36"
                      placeholder="Actual amount (SEK)"
                      value={d.amount ?? cell.actual_amount ?? ""}
                      onChange={(e) => setActualDrafts({ ...actualDrafts, [m]: { amount: e.target.value } })}
                      data-testid={`source-actual-input-${m}`}
                    />
                    <Button size="sm" className="h-8 bg-[#0052CC] hover:bg-[#0047B3]" onClick={() => saveActual(m)} data-testid={`source-actual-save-${m}`}>
                      Save actual
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {canEdit && (
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => onArchive(source)} data-testid="source-archive-button">
              {source.active ? "Deactivate source" : "Activate source"}
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="source-panel-close">Done</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
